import { useState } from 'react';
import Header from './components/Header';
import CustomerPanel from './components/CustomerPanel';
import ScanBasketArea from './components/ScanBasketArea';
import TotalsPanel from './components/TotalsPanel';
import ActionButtons from './components/ActionButtons';
import StatusBar from './components/StatusBar';
import TenderModal from './components/TenderModal';
import ReturnModal from './components/ReturnModal';
import SupervisorModal from './components/SupervisorModal';
import PrintReceiptModal from './components/PrintReceiptModal';
import ParkSaleModal from './components/ParkSaleModal';
import OrderDiscountModal from './components/OrderDiscountModal';
import ExchangeModal from './components/ExchangeModal';
import ItemNoteModal from './components/ItemNoteModal';

export interface BasketItem {
  line: number;
  code: string;
  name: string;
  qty: number;
  price: number;
  discount: boolean;
  tax: boolean;
  total: number;
  note?: string;
}

export interface OrderDiscount {
  type: 'percentage' | 'fixed';
  value: number;
}

export default function POSCheckout() {
  const [mode, setMode] = useState<'sale' | 'tender' | 'return'>('sale');
  const [selectedLineIndex, setSelectedLineIndex] = useState<number | null>(null);
  const [showTenderModal, setShowTenderModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showSupervisorModal, setShowSupervisorModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showParkSaleModal, setShowParkSaleModal] = useState(false);
  const [showOrderDiscountModal, setShowOrderDiscountModal] = useState(false);
  const [showExchangeModal, setShowExchangeModal] = useState(false);
  const [showItemNoteModal, setShowItemNoteModal] = useState(false);
  const [parkSaleInitialTab, setParkSaleInitialTab] = useState<'park' | 'retrieve'>('park');
  const [supervisorAction, setSupervisorAction] = useState('');
  const [orderDiscount, setOrderDiscount] = useState<OrderDiscount | null>(null);
  const [clearCustomerTrigger, setClearCustomerTrigger] = useState(0);
  const [paymentMethods, setPaymentMethods] = useState<Array<{ method: string; amount: number }>>([]);
  const [basketItems, setBasketItems] = useState<BasketItem[]>([]);

  // Calculate grand total for tender modal
  const subtotal = basketItems.reduce((sum, item) => sum + item.total, 0);
  const itemDiscounts = basketItems
    .filter(item => item.discount)
    .reduce((sum, item) => sum + (item.price * item.qty * 0.1), 0);
  
  let orderDiscountAmount = 0;
  if (orderDiscount) {
    if (orderDiscount.type === 'percentage') {
      orderDiscountAmount = (subtotal - itemDiscounts) * (orderDiscount.value / 100);
    } else {
      orderDiscountAmount = orderDiscount.value;
    }
  }
  
  const subtotalAfterDiscounts = subtotal - itemDiscounts - orderDiscountAmount;
  const tax = basketItems
    .filter(item => item.tax)
    .reduce((sum, item) => {
      const itemSubtotal = item.total;
      const itemAfterDiscount = itemSubtotal * (subtotalAfterDiscounts / subtotal);
      return sum + (itemAfterDiscount * 0.09);
    }, 0);
  
  const bagFee = basketItems.length > 0 ? 0.25 : 0;
  
  // Calculate rounding (round to nearest 5 cents)
  const totalBeforeRounding = subtotalAfterDiscounts + tax + bagFee;
  const roundedTotal = Math.round(totalBeforeRounding * 20) / 20; // Round to nearest 0.05
  const rounding = roundedTotal - totalBeforeRounding;
  const grandTotal = roundedTotal;

  const handleTender = () => {
    setShowTenderModal(true);
    setMode('tender');
  };

  const handleReturn = () => {
    setShowReturnModal(true);
    setMode('return');
  };

  const handleSupervisorAction = (action: string) => {
    setSupervisorAction(action);
    setShowSupervisorModal(true);
  };

  const handlePrint = () => {
    setShowPrintModal(true);
  };

  const handleCloseTender = () => {
    setShowTenderModal(false);
    setMode('sale');
  };

  const handlePaymentComplete = (methods: Array<{ method: string; amount: number }>) => {
    // Store payment methods and open print modal
    setPaymentMethods(methods);
    setShowPrintModal(true);
  };

  const handleCloseReturn = () => {
    setShowReturnModal(false);
    setMode('sale');
  };

  const handleCloseSupervisor = () => {
    setShowSupervisorModal(false);
    setSupervisorAction('');
  };

  const handleClosePrint = () => {
    setShowPrintModal(false);
  };

  const handleParkSale = () => {
    setParkSaleInitialTab('park');
    setShowParkSaleModal(true);
  };

  const handleCloseParkSale = () => {
    setShowParkSaleModal(false);
  };

  const handleParkSuccess = () => {
    // Clear the basket after parking
    setBasketItems([]);
    setSelectedLineIndex(null);
  };

  const handleRetrieveSale = (items: BasketItem[]) => {
    // Load the retrieved items into the basket
    setBasketItems(items);
    setSelectedLineIndex(null);
  };

  const handleRecall = () => {
    setParkSaleInitialTab('retrieve');
    setShowParkSaleModal(true);
  };

  const handleOrderDiscount = () => {
    setShowOrderDiscountModal(true);
  };

  const handleCloseOrderDiscount = () => {
    setShowOrderDiscountModal(false);
  };

  const handleApplyOrderDiscount = (discount: OrderDiscount) => {
    setOrderDiscount(discount);
  };

  const handleRemoveOrderDiscount = () => {
    setOrderDiscount(null);
  };

  const handleCancelTransaction = () => {
    // Clear all basket items and reset to new sale screen
    setBasketItems([]);
    setSelectedLineIndex(null);
    setOrderDiscount(null);
    setMode('sale');
    // Trigger customer clear in CustomerPanel
    setClearCustomerTrigger(prev => prev + 1);
  };

  const handleExchangeComplete = (returnItems: BasketItem[], newItems: BasketItem[]) => {
    // Remove return items from basket (create negative entries)
    const returnEntries = returnItems.map((item, index) => ({
      ...item,
      line: basketItems.length + index + 1,
      qty: -item.qty,
      total: -item.total,
    }));

    // Add new items to basket
    const newEntries = newItems.map((item, index) => ({
      ...item,
      line: basketItems.length + returnItems.length + index + 1,
    }));

    setBasketItems([...basketItems, ...returnEntries, ...newEntries]);
    setShowExchangeModal(false);
  };

  const handleExchange = () => {
    setShowExchangeModal(true);
  };

  const handleAddItemNote = () => {
    if (selectedLineIndex !== null) {
      setShowItemNoteModal(true);
    }
  };

  const handleSaveItemNote = (note: string) => {
    if (selectedLineIndex !== null) {
      setBasketItems(prev => prev.map((item, index) => 
        index === selectedLineIndex ? { ...item, note } : item
      ));
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      <Header />
      
      <div className="flex-1 flex overflow-hidden">
        <CustomerPanel clearTrigger={clearCustomerTrigger} />
        
        <ScanBasketArea 
          selectedLineIndex={selectedLineIndex}
          onSelectLine={setSelectedLineIndex}
          basketItems={basketItems}
          setBasketItems={setBasketItems}
        />
        
        <div className="flex flex-col w-96">
          <TotalsPanel 
            basketItems={basketItems} 
            orderDiscount={orderDiscount}
            onRemoveDiscount={handleRemoveOrderDiscount}
          />
          <ActionButtons 
            selectedLineIndex={selectedLineIndex}
            onTender={handleTender}
            onReturn={handleReturn}
            onSupervisorAction={handleSupervisorAction}
            onPrint={handlePrint}
            onParkSale={handleParkSale}
            onRecall={handleRecall}
            onOrderDiscount={handleOrderDiscount}
            onCancelTransaction={handleCancelTransaction}
            onExchange={handleExchange}
            onAddItemNote={handleAddItemNote}
          />
        </div>
      </div>

      <StatusBar mode={mode} />

      {showTenderModal && (
        <TenderModal 
          onClose={handleCloseTender}
          onPaymentComplete={handlePaymentComplete}
          grandTotal={grandTotal}
        />
      )}

      {showReturnModal && (
        <ReturnModal onClose={handleCloseReturn} />
      )}

      {showSupervisorModal && (
        <SupervisorModal 
          action={supervisorAction}
          onClose={handleCloseSupervisor}
        />
      )}

      {showPrintModal && (
        <PrintReceiptModal 
          onClose={handleClosePrint}
          paymentMethods={paymentMethods}
          basketItems={basketItems}
          subtotal={subtotal}
          discount={itemDiscounts + orderDiscountAmount}
          tax={tax}
          total={grandTotal}
        />
      )}

      {showParkSaleModal && (
        <ParkSaleModal 
          onClose={handleCloseParkSale}
          basketItems={basketItems}
          onParkSuccess={handleParkSuccess}
          onRetrieveSale={handleRetrieveSale}
          initialTab={parkSaleInitialTab}
        />
      )}

      {showOrderDiscountModal && (
        <OrderDiscountModal 
          onClose={handleCloseOrderDiscount}
          onApply={handleApplyOrderDiscount}
          currentDiscount={orderDiscount}
        />
      )}

      {showExchangeModal && (
        <ExchangeModal
          onClose={() => setShowExchangeModal(false)}
          onExchangeComplete={handleExchangeComplete}
        />
      )}

      {showItemNoteModal && selectedLineIndex !== null && (
        <ItemNoteModal
          onClose={() => setShowItemNoteModal(false)}
          onSave={handleSaveItemNote}
          itemName={basketItems[selectedLineIndex]?.name || ''}
          currentNote={basketItems[selectedLineIndex]?.note}
        />
      )}
    </div>
  );
}
